import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { useAgent } from "agents/react";
import { useAgentChat } from "@cloudflare/ai-chat/react";
import { useClerk } from "@clerk/clerk-react";
import ReactMarkdown from "react-markdown";

import { SessionSidebar } from "@/components/SessionSidebar";
import { renderToolPart } from "@/components/widgets";
import { useApi } from "@/lib/api";
import { useAgentQueryToken } from "@/lib/agent-token";

interface CoachState {
  pending_check_in?: { message: string; ts: number } | null;
}

export default function CoachPage(props: { userId: string; email: string }) {
  const { sessionId: sessionIdParam } = useParams();
  const nav = useNavigate();
  const api = useApi();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    if (sessionIdParam) return;
    api<{ session_id: string }>("/api/chat/sessions/default")
      .then((r) => nav(`/coach/${r.session_id}`, { replace: true }))
      .catch(() => {});
  }, [sessionIdParam, api, nav]);

  if (!sessionIdParam) {
    return <div className="grid h-full place-items-center text-neutral-500">Loading…</div>;
  }

  return (
    <div className="flex h-full">
      <div className="hidden md:block">
        <SessionSidebar currentId={sessionIdParam} />
      </div>
      {sidebarOpen && (
        <>
          <div className="fixed inset-0 z-30 bg-black/30 md:hidden" onClick={() => setSidebarOpen(false)} />
          <div className="fixed inset-y-0 left-0 z-40 md:hidden">
            <SessionSidebar currentId={sessionIdParam} onClose={() => setSidebarOpen(false)} />
          </div>
        </>
      )}

      <div className="flex flex-1 flex-col overflow-hidden">
        <Stage
          key={sessionIdParam}
          userId={props.userId}
          sessionId={sessionIdParam}
          email={props.email}
          onOpenSidebar={() => setSidebarOpen(true)}
        />
      </div>
    </div>
  );
}

function Stage(props: { userId: string; sessionId: string; email: string; onOpenSidebar: () => void }) {
  const query = useAgentQueryToken();

  // useAgent's onStateUpdate fires whenever the DO broadcasts a state change
  // (after the agent's set_scene tool call updates this.setState() server-
  // side). Capture it into local React state to render banners on changes.
  const [coachState, setCoachState] = useState<CoachState | null>(null);
  const agent = useAgent<CoachState>({
    agent: "CoachAgent",
    name: `coach-${props.userId}-${props.sessionId}`,
    query,
    onStateUpdate: (s) => setCoachState(s),
  });

  const { messages, sendMessage, status, stop } = useAgentChat({ agent });
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const clerk = useClerk();

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  function send(text: string) {
    if (!text.trim()) return;
    sendMessage({ text });
  }

  function submitInput() {
    const text = inputRef.current?.value.trim();
    if (!text) return;
    sendMessage({ text });
    if (inputRef.current) inputRef.current.value = "";
  }

  return (
    <>
      <header className="flex shrink-0 items-center justify-between border-b border-neutral-200 px-4 py-3">
        <div className="flex items-center gap-3">
          <button
            onClick={props.onOpenSidebar}
            className="rounded-lg border border-neutral-200 p-1.5 text-neutral-600 md:hidden"
            aria-label="Open chat list"
          >
            ☰
          </button>
          <div>
            <div className="text-sm font-semibold">Cogi</div>
            <div className="text-xs text-neutral-500">{props.email}</div>
          </div>
        </div>
        <nav className="flex items-center gap-3 text-sm">
          <Link to="/today" className="text-neutral-700">Today</Link>
          <Link to="/journey" className="text-neutral-700">Journey</Link>
          <Link to="/me" className="text-neutral-700">You</Link>
          <Link to="/assets" className="text-neutral-700">Library</Link>
          <button onClick={() => clerk.signOut()} className="text-neutral-500">Sign out</button>
        </nav>
      </header>

      {coachState?.pending_check_in && (
        <div className="border-b border-amber-200 bg-amber-50 px-4 py-2.5">
          <div className="mx-auto max-w-2xl text-xs text-amber-800">
            <span className="mr-2 font-medium">Cogi left you a note</span>
            <span>{coachState.pending_check_in.message}</span>
          </div>
        </div>
      )}

      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4">
        {messages.length === 0 ? (
          <div className="mx-auto mt-12 max-w-md space-y-3 text-center">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-neutral-900 text-2xl text-white">
              🌱
            </div>
            <p className="text-base text-neutral-900">Hi, I'm Cogi.</p>
            <p className="text-sm text-neutral-500">
              Say anything to start —<br />
              <em className="text-neutral-700">"I haven't been sleeping well"</em> · <em className="text-neutral-700">"I want to change something"</em> · <em className="text-neutral-700">"hi"</em>
            </p>
          </div>
        ) : (
          <ul className="mx-auto max-w-2xl space-y-3">
            {messages.map((m) => (
              <li key={m.id} className={m.role === "user" ? "flex justify-end" : "flex justify-start"}>
                <div
                  className={
                    m.role === "user"
                      ? "max-w-[78%] rounded-2xl bg-neutral-900 px-4 py-2.5 text-white"
                      : "max-w-[88%] rounded-2xl bg-white px-4 py-2.5 text-neutral-900 shadow-sm"
                  }
                >
                  {m.parts.map((p, i) => {
                    if (p.type === "text") {
                      return (
                        <div key={i} className="prose prose-sm max-w-none">
                          <ReactMarkdown>{p.text}</ReactMarkdown>
                        </div>
                      );
                    }
                    if (p.type.startsWith("tool-")) {
                      return (
                        <div key={i} className="my-1">
                          {renderToolPart(p as unknown as { type: string; toolCallId: string; state: string }, send)}
                        </div>
                      );
                    }
                    return null;
                  })}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="shrink-0 border-t border-neutral-200 bg-white p-3">
        <div className="mx-auto flex max-w-2xl items-end gap-2">
          <textarea
            ref={inputRef}
            rows={1}
            placeholder="Say something…"
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                submitInput();
              }
            }}
            className="min-h-11 flex-1 resize-none rounded-2xl border border-neutral-200 px-4 py-2.5 outline-none focus:border-neutral-400"
          />
          {status === "streaming" || status === "submitted" ? (
            <button
              onClick={() => stop()}
              className="h-11 rounded-2xl bg-neutral-200 px-4 text-sm"
            >
              Stop
            </button>
          ) : (
            <button
              onClick={submitInput}
              className="h-11 rounded-2xl bg-neutral-900 px-4 text-sm font-medium text-white"
            >
              Send
            </button>
          )}
        </div>
      </div>
    </>
  );
}
